/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Animated,
  useWindowDimensions,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  setFormValue,
  setNotes,
  setMetGoal,
  setValidationErrors,
} from "../../store/slices/pulseCheckSlice";
import { useTheme } from "../../theme";
import { ThemeColors } from "../../theme/types";
import LHSectionHeader from "../../components/LHSectionHeader";
import LhMetricCard from "../../components/LhMetricCard";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { getMetricCardWidth } from "../../utils/getMetricCardWidth";

interface PulseSecondCheckProps {}

// Note: the selected Success/Failure button text color is asserted as the
// literal string "white" by existing tests, so that value is left as-is.
// The disabled notes-input colors (#DADADA / #B1B1B1 / #2D2D2D) are also
// asserted by literal value. Everything else resolves from the shared
// theme so this card reads correctly in both Light and Dark mode, including
// accessible green/red semantic tokens for the Success/Failure control.
const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    cardContainer: {
      marginBottom: 24,
      borderRadius: 6,
      overflow: 'hidden',
      elevation: 4,
      shadowColor: 'rgba(0, 94, 192, 0.08)',
      shadowOffset: {
        width: 4,
        height: 0,
      },
      shadowOpacity: 1,
      shadowRadius: 24,
    },
    secondCheckHeader: {
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    header1text: {
      fontSize: 16,
    },
    metricCardContainer: {
      borderBottomLeftRadius: 6,
      borderBottomRightRadius: 6,
      padding: 16,
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderTopWidth: 0,
      borderColor: colors.border.default,
    },
    metricRowWrapper: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
      gap: Platform.OS === "web" ? 16 : 12,
    }, // Using an approach that works for web cursor styles in React Native
    disabledCursor:
      Platform.OS === "web"
        ? ({
            cursor: "not-allowed",
            outline: "none",
            WebkitTapHighlightColor: "transparent",
          } as any)
        : {},
    metricViewHeader: {
      backgroundColor: colors.text.disabled, // Grayed header to indicate read-only status
      padding: 8,
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      marginBottom: -15,
      zIndex: 1,
    },
    headerText: {
      color: colors.text.inverse,
      fontSize: 14,
      fontWeight: "600",
      textAlign: "center",
    },
    metGoalContainer: {
      width: Platform.OS === "web" ? "32%" : "100%",
      alignItems: "center",
    },
    metGoalText: {
      marginTop: 2,
      fontWeight: "bold",
      marginBottom: 2,
      color: colors.text.primary,
    },
    metGoalRequired: {
      color: colors.status.error,
      fontSize: 12,
      marginTop: 2,
    },
    buttonTouch: {
      flexDirection: "row",
      width: "100%",
      backgroundColor: colors.background.elevated,
      paddingVertical: 10,
      paddingHorizontal: 0,
      borderRadius: 6,
      justifyContent: "space-between",
      alignItems: "center",
      borderWidth: 1,
      borderColor: colors.border.default,
    },
    goalOption: {
      flex: 1,
      paddingVertical: 10,
      borderRadius: 6,
      alignItems: "center",
      justifyContent: "center",
    },
    goalTextOption: {
      fontWeight: "600",
    },
    notesInput: {
      borderWidth: 1,
      borderColor: colors.input.border,
      borderRadius: 6,
      marginTop: Platform.OS === "web" ? "1%" : 30,
      padding: 12,
      minHeight: 100,
      backgroundColor: colors.input.background,
      color: colors.text.primary,
    },
    notesInputDisabled: {
      backgroundColor: "#DADADA",
      borderColor: "#B1B1B1",
      color: "#2D2D2D",
    },
  });

const PulseSecondCheck: React.FC<PulseSecondCheckProps> = () => {
  const dispatch = useDispatch();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const formValues = useSelector(
    (state: RootState) => state.pulseCheck.formValues
  );
  const notes = useSelector((state: RootState) => state.pulseCheck.notes);
  const metGoal = useSelector((state: RootState) => state.pulseCheck.metGoal);
  const validationErrors = useSelector(
    (state: RootState) => state.pulseCheck.validationErrors
  );
  const pulseState = useSelector(
    (state: RootState) => state.pulseCheck.pulseState
  );
  // Add animation values for error highlighting
  const errorAnimations = {
    // metric2 removed as it's now populated from metric1
    result2: useRef(new Animated.Value(0)).current,
  };
  const { width: screenWidth } = useWindowDimensions();

  const cardWidth = React.useMemo(
    () => getMetricCardWidth(screenWidth),
    [screenWidth]
  );
  // Trigger animation when validation errors change
  useEffect(() => {
    Object.keys(errorAnimations).forEach((field) => {
      if (validationErrors[field]) {
        Animated.sequence([
          Animated.timing(
            errorAnimations[field as keyof typeof errorAnimations],
            {
              toValue: 1,
              duration: 300,
              useNativeDriver: false,
            }
          ),
          Animated.timing(
            errorAnimations[field as keyof typeof errorAnimations],
            {
              toValue: 0.7,
              duration: 200,
              useNativeDriver: false,
            }
          ),
          Animated.timing(
            errorAnimations[field as keyof typeof errorAnimations],
            {
              toValue: 1,
              duration: 200,
              useNativeDriver: false,
            }
          ),
        ]).start();
      } else {
        // Reset animation if error is cleared
        errorAnimations[field as keyof typeof errorAnimations].setValue(0);
      }
    });
  }, [validationErrors]);
  const handleFormChange = (field: string, value: string): void => {
    dispatch(setFormValue({ field, value }));

    // Clear validation error for this field if it was previously flagged
    if (validationErrors[field]) {
      const updatedErrors = { ...validationErrors };
      delete updatedErrors[field];
      dispatch(setValidationErrors(updatedErrors));

      // Play a success animation when field becomes valid
      if (value.trim() !== "") {
        // Reset the animation value first
        errorAnimations[field as keyof typeof errorAnimations]?.setValue(0);

        // Trigger a subtle success animation
        Animated.sequence([
          Animated.timing(
            errorAnimations[field as keyof typeof errorAnimations],
            {
              toValue: 0.5,
              duration: 200,
              useNativeDriver: false,
            }
          ),
          Animated.timing(
            errorAnimations[field as keyof typeof errorAnimations],
            {
              toValue: 0,
              duration: 300,
              useNativeDriver: false,
            }
          ),
        ]).start();
      }
    }
  };

  const handleNotesChange = (value: string): void => {
    dispatch(setNotes({ which: "second", value }));
  };

  const handleMetGoal = (value: boolean): void => {
    dispatch(setMetGoal(value));
    // Clear validation error for metGoal if it was previously flagged
    if (validationErrors.metGoal) {
      const updatedErrors = { ...validationErrors };
      delete updatedErrors.metGoal;
      dispatch(setValidationErrors(updatedErrors));
    }
  };

  return (
    <View style={styles.cardContainer}>
      <LHSectionHeader
        title="Second Check"
        containerStyle={styles.secondCheckHeader}
        textStyle={styles.header1text}
      />
      <View style={styles.metricCardContainer}>
        <View style={styles.metricRowWrapper}>
          <View
            style={[
              {
                // No animations needed for read-only field
                opacity: 0.8,
              },
              styles.disabledCursor,
            ]}
          >
            <LhMetricCard
              headerTitle="Previous Metric / Topic"
              inputValue={formValues.metric1 || ""}
              onInputChange={() => {}} // No-op function since this is read-only
              width={cardWidth}
              height={Platform.OS === "web" ? 80 : 75}
              inputHeight={Platform.OS === "web" ? 40 : 36}
              placeholder="No previous metrics"
              fieldId="metric1-readonly-field"
              disabled={true} // Always disabled to make it read-only
            />
          </View>
          <Animated.View
            style={{
              opacity: Animated.add(
                0.8,
                errorAnimations.result2.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 0.2],
                })
              ),
              transform: [
                {
                  scale: errorAnimations.result2.interpolate({
                    inputRange: [0, 1],
                    outputRange: [1, 1.02],
                  }),
                },
              ],
            }}
          >
            <LhMetricCard
              headerTitle="Results"
              inputValue={formValues.result2}
              onInputChange={(v) => handleFormChange("result2", v)}
              width={cardWidth}
              height={Platform.OS === "web" ? 80 : 75}
              inputHeight={Platform.OS === "web" ? 40 : 36}
              hasError={validationErrors.result2}
              fieldId="result2-field"
              disabled={pulseState === "completed"}
            />
          </Animated.View>
          <View style={styles.metGoalContainer}>
            <Text style={styles.metGoalText}>Met Goal? </Text>
            {validationErrors.metGoal && (
              <Text style={styles.metGoalRequired}>This field is required</Text>
            )}
            <View style={styles.buttonTouch}>
              <TouchableOpacity
                onPress={
                  pulseState === 'completed'
                    ? undefined
                    : () => handleMetGoal(true)
                }
                style={[
                  styles.goalOption,
                  {
                    backgroundColor:
                      metGoal === true ? colors.status.success : "transparent",
                    borderColor: validationErrors.metGoal
                      ? colors.status.error
                      : "transparent",
                    borderWidth: validationErrors.metGoal ? 1 : 0,
                  },
                ]}
                disabled={pulseState === "completed"}
              >
                <Text
                  style={[
                    styles.goalTextOption,
                    {
                      color: metGoal === true ? 'white' : colors.text.primary,
                    },
                  ]}
                >
                  Success
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={
                  pulseState === 'completed'
                    ? undefined
                    : () => handleMetGoal(false)
                }
                style={[
                  styles.goalOption,
                  {
                    backgroundColor:
                      metGoal === false ? colors.status.error : "transparent",
                    borderColor: validationErrors.metGoal
                      ? colors.status.error
                      : "transparent",
                    borderWidth: validationErrors.metGoal ? 1 : 0,
                  },
                ]}
                disabled={pulseState === "completed"}
              >
                <Text
                  style={[
                    styles.goalTextOption,
                    {
                      color:
                        metGoal === false ? 'white' : colors.text.primary,
                    },
                  ]}
                >
                  Failure
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
        <TextInput
          style={[
            styles.notesInput,
            pulseState === "completed" && styles.notesInputDisabled,
          ]}
          placeholder="Notes from second conversation..."
          placeholderTextColor={colors.text.secondary}
          multiline
          value={notes.second}
          onChangeText={handleNotesChange}
          editable={pulseState !== "completed"}
        />
      </View>
    </View>
  );
};

export default PulseSecondCheck;
